/**
 * Runtime validators for the canonical schemas (R5).
 *
 * These are dependency-free "JSON-schema validators": each takes an `unknown`
 * value (e.g. a JSON document freshly parsed from S3 or submitted by a client)
 * and structurally validates it against the corresponding schema in
 * {@link ./schemas.ts}, returning a discriminated {@link ValidationResult}. They
 * are the runtime counterpart to the compile-time types and are the guard the
 * authorized write path (task 6) runs before applying any mutation (R4.2, R4.3).
 *
 * Why hand-written rather than a JSON-schema library: shared-types is imported
 * by every other package (Lambdas, infra, srd-content) and is intentionally
 * dependency-free, matching the existing convention (srd-content ships plain
 * TS + accessors, no runtime deps). These validators enforce the same
 * constraints a JSON Schema would — required fields, types, enums, and the
 * cross-field invariants R5/R9/R12/R18/R21 call out — with zero install cost.
 *
 * SRD *legality* (e.g. whether a level-up is legal per the class tables) is a
 * separate, deeper check owned by the write-path validator (task 6) using
 * @rufus/srd-content; these validators enforce structural + schema correctness.
 */
import { CONDITION_NAMES, } from "./schemas.js";
import { WEATHER_KINDS } from "./schemas.js";
import { SCHEMA_VERSION } from "./index.js";
// ---------------------------------------------------------------------------
// Constants used by the validators
// ---------------------------------------------------------------------------
const ABILITY_KEYS = ["str", "dex", "con", "int", "wis", "cha"];
const ENTITY_TYPES = ["human_pc", "ai_pc", "npc"];
const INTERACTION_MODES = ["free_flow", "combat"];
const RANGE_BANDS = ["melee", "near", "far"];
const ADVANCEMENT_MODES = ["xp", "milestone"];
const ENCOUNTER_STATUSES = ["active", "resolved"];
const conditionNameSet = new Set(CONDITION_NAMES);
// Arc model enums (§3.5a / §3.5b) — kept here so the validator and the schema
// enum unions cannot drift silently.
const ARC_KINDS = ["long", "short", "side", "character"];
const ARC_SCOPES = ["long", "medium", "short"];
const ARC_STATUSES = [
    "candidate",
    "active",
    "resolved",
    "failed",
    "abandoned",
    "mutated",
];
const ARC_RELEVANCES = ["spine", "advances-spine", "optional"];
const ARC_RESOLUTIONS = ["sketch", "developing", "concrete"];
const BEAT_TYPES = ["action", "mystery", "social", "downtime", "climax"];
const BEAT_STATUSES = ["pending", "done", "failed", "skipped"];
const PLACE_KINDS = ["room", "dungeon", "town", "region"];
const FINALE_TIERS = ["full-success", "partial", "failure", "pyrrhic"];
const BRANCH_STATUSES = ["open", "taken", "pruned"];
const SEED_STATUSES = ["planted", "payoff-pending", "paid-off"];
const ARC_CREATED_BY = ["author", "dm", "player-input"];
// Issue triage statuses (R-issues) — kept here so the validator and the schema
// enum union cannot drift silently.
const ISSUE_STATUSES = [
    "open",
    "investigating",
    "partially-resolved",
    "resolved",
];
/** Issue origin enum (R-telemetry) — mirror of the IssueSource union. */
const ISSUE_SOURCES = ["user", "system"];
/** SRD 5.1 damage types (engine-owned-mechanics R7) — mirror of the DamageType union. */
const DAMAGE_TYPE_SET = new Set([
    "acid",
    "bludgeoning",
    "cold",
    "fire",
    "force",
    "lightning",
    "necrotic",
    "piercing",
    "poison",
    "psychic",
    "radiant",
    "slashing",
    "thunder",
]);
const DAMAGE_MODIFIER_KINDS = ["resistance", "vulnerability", "immunity"];
const DAMAGE_MODIFIER_PERMANENCE = ["permanent", "temporary"];
const ADVANTAGE_SOURCE_ROLL_TYPES = ["save", "check", "attack"];
const ADVANTAGE_SOURCE_KINDS = ["advantage", "disadvantage"];
const REROLL_SOURCE_TRIGGERS = ["d20"];
const REROLL_SOURCE_RULES = ["reroll1"];
const RESOURCE_REFRESH_KINDS = ["short", "long", "none"];
const ACTIVE_EFFECT_SOURCE_KINDS = ["spell", "feature", "feat", "innate"];
const APPLIED_CHANGE_KINDS = ["condition", "buff", "setAc", "maxHpBonus"];
const ONGOING_EFFECT_TRIGGERS = ["casterTurn", "entersOrStartsInArea"];
// ---------------------------------------------------------------------------
// Small internal check helpers — each pushes onto a shared error accumulator.
// ---------------------------------------------------------------------------
class Checker {
    errors = [];
    fail(path, message) {
        this.errors.push({ path, message });
    }
    /** Returns true if `v` is a plain (non-array, non-null) object. */
    isObject(v, path) {
        if (typeof v !== "object" || v === null || Array.isArray(v)) {
            this.fail(path, "expected an object");
            return false;
        }
        return true;
    }
    string(v, path) {
        if (typeof v !== "string") {
            this.fail(path, "expected a string");
            return false;
        }
        return true;
    }
    /** Non-empty string. */
    id(v, path) {
        if (typeof v !== "string" || v.length === 0) {
            this.fail(path, "expected a non-empty string");
            return false;
        }
        return true;
    }
    nullableString(v, path) {
        if (v !== null && typeof v !== "string") {
            this.fail(path, "expected a string or null");
            return false;
        }
        return true;
    }
    number(v, path) {
        if (typeof v !== "number" || Number.isNaN(v)) {
            this.fail(path, "expected a number");
            return false;
        }
        return true;
    }
    integer(v, path) {
        if (!this.number(v, path))
            return false;
        if (!Number.isInteger(v)) {
            this.fail(path, "expected an integer");
            return false;
        }
        return true;
    }
    nonNegativeInt(v, path) {
        if (!this.integer(v, path))
            return false;
        if (v < 0) {
            this.fail(path, "expected a non-negative integer");
            return false;
        }
        return true;
    }
    boolean(v, path) {
        if (typeof v !== "boolean") {
            this.fail(path, "expected a boolean");
            return false;
        }
        return true;
    }
    array(v, path) {
        if (!Array.isArray(v)) {
            this.fail(path, "expected an array");
            return false;
        }
        return true;
    }
    enumOf(v, allowed, path) {
        if (typeof v !== "string" || !allowed.includes(v)) {
            this.fail(path, `expected one of: ${allowed.join(", ")}`);
            return false;
        }
        return true;
    }
    schemaVersion(v, path) {
        if (v !== SCHEMA_VERSION) {
            this.fail(path, `expected schemaVersion === ${SCHEMA_VERSION}`);
        }
    }
}
function finish(checker, value) {
    if (checker.errors.length > 0) {
        return { valid: false, errors: checker.errors };
    }
    return { valid: true, value: value, errors: [] };
}
// ---------------------------------------------------------------------------
// Shared sub-validators
// ---------------------------------------------------------------------------
function checkAbilityScores(c, v, path) {
    if (!c.isObject(v, path))
        return;
    for (const key of ABILITY_KEYS) {
        if (!(key in v)) {
            c.fail(`${path}.${key}`, "missing ability score");
            continue;
        }
        c.integer(v[key], `${path}.${key}`);
    }
}
function checkPool(c, v, path) {
    if (!c.isObject(v, path))
        return;
    c.nonNegativeInt(v.max, `${path}.max`);
    c.nonNegativeInt(v.remaining, `${path}.remaining`);
    if (typeof v.max === "number" && typeof v.remaining === "number" && v.remaining > v.max) {
        c.fail(`${path}.remaining`, "remaining must not exceed max");
    }
    // Optional per-rest refresh binding (engine-owned-mechanics R2c.3) — only
    // meaningful on classResources entries, but accepted structurally anywhere a
    // Pool appears. Additive + optional: absence is valid (reads as "none").
    if ("refresh" in v && v.refresh !== undefined) {
        c.enumOf(v.refresh, RESOURCE_REFRESH_KINDS, `${path}.refresh`);
    }
}
/**
 * Validate a map of {@link Pool}s. `allowedKeys`, when supplied, constrains the
 * key domain (used for spellSlots, which is documented as keyed by spell level
 * `"1".."9"`); hitDice / classResources have free-form keys and pass it undefined.
 */
function checkPoolMap(c, v, path, allowedKeys) {
    if (!c.isObject(v, path))
        return;
    for (const [key, pool] of Object.entries(v)) {
        if (allowedKeys && !allowedKeys.has(key)) {
            c.fail(`${path}.${key}`, `unexpected key: expected one of ${[...allowedKeys].join(", ")}`);
        }
        checkPool(c, pool, `${path}.${key}`);
    }
}
/** The legal spell-slot level keys (R5.1: spell slots keyed by level "1".."9"). */
const SPELL_SLOT_LEVEL_KEYS = new Set([
    "1", "2", "3", "4", "5", "6", "7", "8", "9",
]);
function checkStringArray(c, v, path) {
    if (!c.array(v, path))
        return;
    v.forEach((item, i) => c.string(item, `${path}[${i}]`));
}
/** A positive (> 0) integer — for calendar fields (minutes/hours/days/months per unit). */
function checkPositiveInt(c, v, path) {
    if (!c.integer(v, path))
        return;
    if (v <= 0)
        c.fail(path, "expected a positive integer");
}
/**
 * Validate the `conditions` array (R6.2), shared by the PC and npc paths so the
 * two never drift. Each entry must name a known SRD condition and carry a
 * source string. `durationRounds` and `endCondition` are the two ways to record
 * how a condition ends: a numeric round counter, an explicit end trigger, or —
 * for a permanent / until-removed condition — NEITHER (both null). What is NOT
 * allowed is BOTH at once (ambiguous end). A non-null `durationRounds` is routed
 * through {@link Checker.number} so a NaN counter is rejected like every other
 * numeric field.
 */
function checkConditions(c, v, path) {
    if (!c.array(v, path))
        return;
    v.forEach((cond, i) => {
        const p = `${path}[${i}]`;
        if (!c.isObject(cond, p))
            return;
        if (!conditionNameSet.has(cond.name)) {
            c.fail(`${p}.name`, `unknown condition name: ${String(cond.name)}`);
        }
        c.string(cond.source, `${p}.source`);
        // durationRounds: a number (NaN rejected via c.number) or null.
        let hasDuration = false;
        if (cond.durationRounds !== null) {
            hasDuration = c.number(cond.durationRounds, `${p}.durationRounds`);
        }
        // endCondition: a string or null.
        let hasEnd = false;
        if (cond.endCondition !== null) {
            hasEnd = c.string(cond.endCondition, `${p}.endCondition`);
        }
        // A condition may carry a round counter OR an end trigger, but not both
        // (that would be an ambiguous end). Carrying neither is legal — it denotes
        // a permanent / until-removed condition.
        if (hasDuration && hasEnd) {
            c.fail(p, "a condition may not carry both durationRounds and endCondition");
        }
    });
}
/**
 * Validate `combat.deathSaves` (engine-owned-mechanics R5) when present and
 * non-null. Three non-negative-integer-ish counters capped at 3 + a boolean.
 * Absence / null is valid (not dying) and is checked by the caller.
 */
function checkDeathSaves(c, v, path) {
    if (!c.isObject(v, path))
        return;
    if (c.nonNegativeInt(v.successes, `${path}.successes`) && v.successes > 3) {
        c.fail(`${path}.successes`, "death-save successes must be 0..3");
    }
    if (c.nonNegativeInt(v.failures, `${path}.failures`) && v.failures > 3) {
        c.fail(`${path}.failures`, "death-save failures must be 0..3");
    }
    c.boolean(v.stable, `${path}.stable`);
}
/** Validate `combat.damageModifiers` (engine-owned-mechanics R7) when present. */
function checkDamageModifiers(c, v, path) {
    if (!c.array(v, path))
        return;
    v.forEach((m, i) => {
        const p = `${path}[${i}]`;
        if (!c.isObject(m, p))
            return;
        if (!DAMAGE_TYPE_SET.has(m.damageType)) {
            c.fail(`${p}.damageType`, `unknown damage type: ${String(m.damageType)}`);
        }
        c.enumOf(m.kind, DAMAGE_MODIFIER_KINDS, `${p}.kind`);
        c.id(m.source, `${p}.source`);
        c.enumOf(m.permanence, DAMAGE_MODIFIER_PERMANENCE, `${p}.permanence`);
        if ("endsAtRound" in m && m.endsAtRound !== undefined && m.endsAtRound !== null) {
            c.integer(m.endsAtRound, `${p}.endsAtRound`);
        }
    });
}
/** Validate `resources.advantageSources` (engine-owned-mechanics R2c.4) when present. */
function checkAdvantageSources(c, v, path) {
    if (!c.array(v, path))
        return;
    v.forEach((s, i) => {
        const p = `${path}[${i}]`;
        if (!c.isObject(s, p))
            return;
        c.enumOf(s.rollType, ADVANTAGE_SOURCE_ROLL_TYPES, `${p}.rollType`);
        c.id(s.trigger, `${p}.trigger`);
        if ("ability" in s && s.ability !== undefined) {
            c.enumOf(s.ability, ABILITY_KEYS, `${p}.ability`);
        }
        if ("kind" in s && s.kind !== undefined) {
            c.enumOf(s.kind, ADVANTAGE_SOURCE_KINDS, `${p}.kind`);
        }
    });
}
/** Validate `resources.rerollSources` (engine-owned-mechanics R9.1) when present. */
function checkRerollSources(c, v, path) {
    if (!c.array(v, path))
        return;
    v.forEach((s, i) => {
        const p = `${path}[${i}]`;
        if (!c.isObject(s, p))
            return;
        c.enumOf(s.trigger, REROLL_SOURCE_TRIGGERS, `${p}.trigger`);
        c.enumOf(s.rule, REROLL_SOURCE_RULES, `${p}.rule`);
        c.id(s.source, `${p}.source`);
    });
}
/** Validate one {@link AppliedChange} inside an active effect. */
function checkAppliedChange(c, v, path) {
    if (!c.isObject(v, path))
        return;
    if (!c.enumOf(v.kind, APPLIED_CHANGE_KINDS, `${path}.kind`))
        return;
    if (v.kind === "condition") {
        if (!conditionNameSet.has(v.name)) {
            c.fail(`${path}.name`, `unknown condition name: ${String(v.name)}`);
        }
    }
    else if (v.kind === "buff") {
        c.id(v.bonus, `${path}.bonus`);
        checkStringArray(c, v.appliesTo, `${path}.appliesTo`);
    }
    else if (v.kind === "setAc") {
        c.integer(v.acDelta, `${path}.acDelta`);
    }
    else if (v.kind === "maxHpBonus") {
        c.integer(v.amount, `${path}.amount`);
    }
}
/**
 * Validate `concentration.ongoingEffect` (engine-owned-mechanics R2.10) when
 * present: a spell id, a non-negative integer slot level, and a known trigger.
 */
function checkOngoingEffect(c, v, path) {
    if (!c.isObject(v, path))
        return;
    c.id(v.spellId, `${path}.spellId`);
    // `atSlotLevel` is a non-negative slot level (0 = cantrip); a negative level is invalid.
    c.nonNegativeInt(v.atSlotLevel, `${path}.atSlotLevel`);
    c.enumOf(v.trigger, ONGOING_EFFECT_TRIGGERS, `${path}.trigger`);
}
/**
 * Validate `resources.inspiration` (engine-owned-mechanics R8.1) when present: a
 * positive-integer die size + a non-empty source string, or `null` (none held).
 */
function checkInspiration(c, v, path) {
    if (v === null)
        return;
    if (!c.isObject(v, path))
        return;
    c.nonNegativeInt(v.die, `${path}.die`);
    if (typeof v.die === "number" && v.die < 1)
        c.fail(`${path}.die`, "inspiration die must be a positive integer");
    c.id(v.source, `${path}.source`);
}
/** Validate `resources.activeEffects` (engine-owned-mechanics R2.9) when present. */
function checkActiveEffects(c, v, path) {
    if (!c.array(v, path))
        return;
    v.forEach((e, i) => {
        const p = `${path}[${i}]`;
        if (!c.isObject(e, p))
            return;
        c.id(e.handle, `${p}.handle`);
        if (c.isObject(e.source, `${p}.source`)) {
            c.enumOf(e.source.kind, ACTIVE_EFFECT_SOURCE_KINDS, `${p}.source.kind`);
            c.id(e.source.id, `${p}.source.id`);
        }
        if (c.array(e.applied, `${p}.applied`)) {
            e.applied.forEach((ch, j) => checkAppliedChange(c, ch, `${p}.applied[${j}]`));
        }
        if (e.endsAtRound !== null) {
            c.integer(e.endsAtRound, `${p}.endsAtRound`);
        }
        c.boolean(e.concentration, `${p}.concentration`);
    });
}
/** Validate `spells.innate` (engine-owned-mechanics R2b.8) when present. */
function checkInnateSpells(c, v, path) {
    if (!c.array(v, path))
        return;
    v.forEach((s, i) => {
        const p = `${path}[${i}]`;
        if (!c.isObject(s, p))
            return;
        c.id(s.spellId, `${p}.spellId`);
        c.enumOf(s.castingAbility, ABILITY_KEYS, `${p}.castingAbility`);
        if (s.availability === "atWill") {
            // ok
        }
        else if (c.isObject(s.availability, `${p}.availability`)) {
            c.enumOf(s.availability.perRest, ["short", "long"], `${p}.availability.perRest`);
            c.nonNegativeInt(s.availability.charges, `${p}.availability.charges`);
        }
    });
}
/**
 * Validate the engine-owned-mechanics OPTIONAL ledger fields (R5, R7, R2b, R2c,
 * R2.9) on a `combat`/`resources`/`spells` block. Every field is ADDITIVE +
 * OPTIONAL — absence is valid and reads as its empty default (deathSaves→null,
 * arrays→[]). Shared by the PC and npc validators so the two never drift. No
 * SCHEMA_VERSION bump (design §2.1).
 */
function checkEngineMechanicsFields(c, input) {
    const combat = input.combat;
    if (combat !== null && typeof combat === "object" && !Array.isArray(combat)) {
        const cb = combat;
        if ("deathSaves" in cb && cb.deathSaves !== undefined && cb.deathSaves !== null) {
            checkDeathSaves(c, cb.deathSaves, "$.combat.deathSaves");
        }
        if ("damageModifiers" in cb && cb.damageModifiers !== undefined) {
            checkDamageModifiers(c, cb.damageModifiers, "$.combat.damageModifiers");
        }
    }
    const resources = input.resources;
    if (resources !== null && typeof resources === "object" && !Array.isArray(resources)) {
        const r = resources;
        if ("advantageSources" in r && r.advantageSources !== undefined) {
            checkAdvantageSources(c, r.advantageSources, "$.resources.advantageSources");
        }
        if ("rerollSources" in r && r.rerollSources !== undefined) {
            checkRerollSources(c, r.rerollSources, "$.resources.rerollSources");
        }
        if ("activeEffects" in r && r.activeEffects !== undefined) {
            checkActiveEffects(c, r.activeEffects, "$.resources.activeEffects");
        }
        if ("inspiration" in r && r.inspiration !== undefined) {
            checkInspiration(c, r.inspiration, "$.resources.inspiration");
        }
    }
    const spells = input.spells;
    if (spells !== null && typeof spells === "object" && !Array.isArray(spells)) {
        const s = spells;
        if ("innate" in s && s.innate !== undefined) {
            checkInnateSpells(c, s.innate, "$.spells.innate");
        }
    }
}
// ---------------------------------------------------------------------------
// Character Ledger (R5.1, R29.1, R9.2)
// ---------------------------------------------------------------------------
/**
 * Validate a full Character Ledger (R5.1). Enforces the identity/ownership
 * fields (R29.1), the portable `knowledge` array (R9.2), and the schemaVersion
 * (R3.5), plus the R5.1 cross-field invariants (e.g. an `npc` has no owner).
 *
 * The lightweight `npc` subset (R5.2) is handled by task 4.2; here an npc must
 * still be structurally valid where it does carry fields, and the fields an
 * npc legally omits (resources/spells/progression/currency/proficiencies) are
 * treated as optional. For a `human_pc`/`ai_pc` those fields are required.
 */
export function validateCharacterLedger(input) {
    const c = new Checker();
    if (!c.isObject(input, "$"))
        return finish(c, input);
    c.schemaVersion(input.schemaVersion, "$.schemaVersion");
    c.id(input.characterId, "$.characterId");
    c.id(input.name, "$.name");
    const isEntity = c.enumOf(input.entityType, ENTITY_TYPES, "$.entityType");
    const entityType = isEntity ? input.entityType : undefined;
    const isNpc = entityType === "npc";
    // Ownership fields (R5.1, R29.1, R28.4). Only a human_pc has an owning player;
    // an ai_pc and an npc are BOTH campaign-owned (ownerUserId: null):
    //   - human_pc — owned + controlled by the human, so ownerUserId must be a
    //     non-null userId (R29.1);
    //   - ai_pc — campaign-owned, AI-run: ownerUserId must be null. An ai_pc that
    //     carried an owner would misrepresent it as human-owned, and the Green
    //     Room hand-back (setControl → ai_pc, R28.4) explicitly sets it to null.
    //     The AI-PC's full-PC completeness is still required via `requireFull`
    //     below — ownerless does NOT mean lightweight.
    //   - npc — campaign-owned, DM/AI-run: ownerUserId must be null (R5.1).
    // So the ONLY entity type that carries an owner is human_pc; both ai_pc and
    // npc must be ownerless. This matches deriveLedger (character creation),
    // createAiPc/createNpc, and checkSetControlLegality (the write path).
    if (c.nullableString(input.ownerUserId, "$.ownerUserId")) {
        if (entityType === "human_pc") {
            if (input.ownerUserId === null) {
                c.fail("$.ownerUserId", "a human_pc must have an ownerUserId (R29.1)");
            }
        }
        else if (entityType === "ai_pc" || isNpc) {
            if (input.ownerUserId !== null) {
                c.fail("$.ownerUserId", `an ${entityType} is campaign-owned and must not carry an ownerUserId`);
            }
        }
    }
    c.nullableString(input.controllerUserId, "$.controllerUserId");
    c.nullableString(input.currentCampaignId, "$.currentCampaignId");
    if ("aiDescriptor" in input)
        c.nullableString(input.aiDescriptor, "$.aiDescriptor");
    // core (R5.1)
    if (c.isObject(input.core, "$.core")) {
        c.nonNegativeInt(input.core.level, "$.core.level");
        c.string(input.core.class, "$.core.class");
        c.string(input.core.ancestry, "$.core.ancestry");
        c.string(input.core.background, "$.core.background");
        checkAbilityScores(c, input.core.abilityScores, "$.core.abilityScores");
        c.integer(input.core.proficiencyBonus, "$.core.proficiencyBonus");
    }
    // combat (R5.1)
    if (c.isObject(input.combat, "$.combat")) {
        c.nonNegativeInt(input.combat.maxHp, "$.combat.maxHp");
        c.integer(input.combat.currentHp, "$.combat.currentHp");
        c.nonNegativeInt(input.combat.tempHp, "$.combat.tempHp");
        c.nonNegativeInt(input.combat.ac, "$.combat.ac");
        // baseAc (Equipment & Armor R4.1) — ADDITIVE + OPTIONAL mundane-AC base;
        // a non-negative integer when present, absent on a legacy ledger.
        if ("baseAc" in input.combat && input.combat.baseAc !== undefined) {
            c.nonNegativeInt(input.combat.baseAc, "$.combat.baseAc");
        }
        c.integer(input.combat.initiativeMod, "$.combat.initiativeMod");
        c.nonNegativeInt(input.combat.speed, "$.combat.speed");
        // baseSpeed (Equipment & Armor Phase E) — ADDITIVE + OPTIONAL base walking
        // speed before any armor penalty; a non-negative integer when present.
        if ("baseSpeed" in input.combat && input.combat.baseSpeed !== undefined) {
            c.nonNegativeInt(input.combat.baseSpeed, "$.combat.baseSpeed");
        }
        if (typeof input.combat.currentHp === "number" &&
            typeof input.combat.maxHp === "number" &&
            input.combat.currentHp > input.combat.maxHp) {
            c.fail("$.combat.currentHp", "currentHp must not exceed maxHp");
        }
    }
    // resources / spells / progression / currency / proficiencies:
    // required for a full PC/AI-PC, optional (omittable) for an npc (R5.2).
    const requireFull = entityType === "human_pc" || entityType === "ai_pc";
    if ("resources" in input && input.resources !== undefined) {
        if (c.isObject(input.resources, "$.resources")) {
            checkPoolMap(c, input.resources.hitDice, "$.resources.hitDice");
            checkPoolMap(c, input.resources.spellSlots, "$.resources.spellSlots", SPELL_SLOT_LEVEL_KEYS);
            checkPoolMap(c, input.resources.classResources, "$.resources.classResources");
        }
    }
    else if (requireFull) {
        c.fail("$.resources", "a human_pc/ai_pc must include resources");
    }
    if ("spells" in input && input.spells !== undefined) {
        if (c.isObject(input.spells, "$.spells")) {
            checkStringArray(c, input.spells.known, "$.spells.known");
            checkStringArray(c, input.spells.prepared, "$.spells.prepared");
        }
    }
    else if (requireFull) {
        c.fail("$.spells", "a human_pc/ai_pc must include spells");
    }
    if ("currency" in input && input.currency !== undefined) {
        if (c.isObject(input.currency, "$.currency")) {
            c.nonNegativeInt(input.currency.gp, "$.currency.gp");
            c.nonNegativeInt(input.currency.sp, "$.currency.sp");
            c.nonNegativeInt(input.currency.cp, "$.currency.cp");
        }
    }
    else if (requireFull) {
        c.fail("$.currency", "a human_pc/ai_pc must include currency");
    }
    if ("proficiencies" in input && input.proficiencies !== undefined) {
        if (c.isObject(input.proficiencies, "$.proficiencies")) {
            checkStringArray(c, input.proficiencies.skills, "$.proficiencies.skills");
            if (c.array(input.proficiencies.saves, "$.proficiencies.saves")) {
                input.proficiencies.saves.forEach((s, i) => c.enumOf(s, ABILITY_KEYS, `$.proficiencies.saves[${i}]`));
            }
            checkStringArray(c, input.proficiencies.weapons, "$.proficiencies.weapons");
            checkStringArray(c, input.proficiencies.tools, "$.proficiencies.tools");
            if ("expertise" in input.proficiencies && input.proficiencies.expertise !== undefined) {
                checkStringArray(c, input.proficiencies.expertise, "$.proficiencies.expertise");
            }
        }
    }
    else if (requireFull) {
        c.fail("$.proficiencies", "a human_pc/ai_pc must include proficiencies");
    }
    if ("progression" in input && input.progression !== undefined) {
        if (c.isObject(input.progression, "$.progression")) {
            c.enumOf(input.progression.mode, ADVANCEMENT_MODES, "$.progression.mode");
            c.nonNegativeInt(input.progression.xp, "$.progression.xp");
            checkStringArray(c, input.progression.milestones, "$.progression.milestones");
        }
    }
    else if (requireFull) {
        c.fail("$.progression", "a human_pc/ai_pc must include progression");
    }
    // inventory (R5.1) — required (possibly empty)
    if (c.array(input.inventory, "$.inventory")) {
        input.inventory.forEach((item, i) => {
            const p = `$.inventory[${i}]`;
            if (!c.isObject(item, p))
                return;
            c.id(item.itemId, `${p}.itemId`);
            c.string(item.name, `${p}.name`);
            c.nonNegativeInt(item.qty, `${p}.qty`);
            c.boolean(item.equipped, `${p}.equipped`);
            c.boolean(item.attunement, `${p}.attunement`);
        });
    }
    // conditions (R5.1, R6.2)
    checkConditions(c, input.conditions, "$.conditions");
    // concentration (R6.3) — object or null
    if (input.concentration !== null) {
        if (c.isObject(input.concentration, "$.concentration")) {
            c.id(input.concentration.spellId, "$.concentration.spellId");
            checkStringArray(c, input.concentration.targets, "$.concentration.targets");
            if (input.concentration.endsAtRound !== null) {
                c.integer(input.concentration.endsAtRound, "$.concentration.endsAtRound");
            }
            // ongoingEffect (R2.10) — optional; when present, validates the recurring shape.
            if (input.concentration.ongoingEffect !== undefined) {
                checkOngoingEffect(c, input.concentration.ongoingEffect, "$.concentration.ongoingEffect");
            }
        }
    }
    // pendingRollContext (R31.16) — object or null
    if (input.pendingRollContext !== null) {
        if (c.isObject(input.pendingRollContext, "$.pendingRollContext")) {
            c.string(input.pendingRollContext.type, "$.pendingRollContext.type");
            c.integer(input.pendingRollContext.dc, "$.pendingRollContext.dc");
            if ("skill" in input.pendingRollContext && input.pendingRollContext.skill !== undefined) {
                c.string(input.pendingRollContext.skill, "$.pendingRollContext.skill");
            }
        }
    }
    // knowledge (R9.2, R29.5) — the character's OWN portable knowledge; required array
    if (c.array(input.knowledge, "$.knowledge")) {
        input.knowledge.forEach((node, i) => {
            const p = `$.knowledge[${i}]`;
            if (!c.isObject(node, p))
                return;
            c.id(node.nodeId, `${p}.nodeId`);
            c.string(node.text, `${p}.text`);
        });
    }
    // engine-owned-mechanics optional fields (R5/R7/R2b/R2c/R2.9) — additive,
    // validated only when present; absence reads as the empty default (§2.1).
    checkEngineMechanicsFields(c, input);
    return finish(c, input);
}
// ---------------------------------------------------------------------------
// Lightweight NPC subset (R5.2)
// ---------------------------------------------------------------------------
/**
 * Validate an `npc` Character Ledger against the LIGHTWEIGHT SUBSET (R5.2).
 *
 * An `npc` is a DM-controlled world character run by Rufus (glossary). It needs
 * only enough state for deterministic combat math and MUST NOT be required to
 * carry full-PC progression. Per the design's Data Model note, an npc requires
 * only: `characterId`, `name`, `entityType` (which MUST be `"npc"`), a minimal
 * `core` (level, class, ancestry, background, ability scores, proficiency bonus),
 * `combat` (HP, AC, core combat values), a basic `inventory`, and `conditions`.
 * It OMITS the full-PC-only fields — `resources`, `spells`, `currency`,
 * `proficiencies`, and `progression` — and, having no owning player, MUST carry
 * a null `ownerUserId` (R5.1).
 *
 * This is a dedicated npc branch rather than a flag on
 * {@link validateCharacterLedger} so the two intents read clearly at the call
 * site: `validateCharacterLedger` for the full PC/AI-PC shape (which requires
 * the progression fields), `validateNpcLedger` for the lightweight subset. Both
 * enforce identical structural rules on the fields an npc *does* carry, so an
 * npc's HP/AC/core stats/inventory are validated exactly as a PC's are.
 *
 * The full-PC-only fields are not merely optional here: if present they must
 * still be structurally well-formed (a malformed `resources` block is a bug),
 * but their ABSENCE is legal for an npc (R5.2).
 */
export function validateNpcLedger(input) {
    const c = new Checker();
    if (!c.isObject(input, "$"))
        return finish(c, input);
    c.schemaVersion(input.schemaVersion, "$.schemaVersion");
    c.id(input.characterId, "$.characterId");
    c.id(input.name, "$.name");
    // entityType MUST be exactly "npc" on this path (R5.2).
    if (input.entityType !== "npc") {
        c.fail("$.entityType", 'expected entityType === "npc" for the lightweight subset');
    }
    // Ownership (R5.1): an npc has no owning player. ownerUserId must be null.
    if (c.nullableString(input.ownerUserId, "$.ownerUserId") && input.ownerUserId !== null) {
        c.fail("$.ownerUserId", "an npc must not have an ownerUserId");
    }
    c.nullableString(input.controllerUserId, "$.controllerUserId");
    c.nullableString(input.currentCampaignId, "$.currentCampaignId");
    if ("aiDescriptor" in input)
        c.nullableString(input.aiDescriptor, "$.aiDescriptor");
    // core (R5.2 — "core stats" required). Minimal set, same shape as a PC.
    if (c.isObject(input.core, "$.core")) {
        c.nonNegativeInt(input.core.level, "$.core.level");
        c.string(input.core.class, "$.core.class");
        c.string(input.core.ancestry, "$.core.ancestry");
        c.string(input.core.background, "$.core.background");
        checkAbilityScores(c, input.core.abilityScores, "$.core.abilityScores");
        c.integer(input.core.proficiencyBonus, "$.core.proficiencyBonus");
    }
    // combat (R5.2 — HP + AC required for deterministic combat math).
    if (c.isObject(input.combat, "$.combat")) {
        c.nonNegativeInt(input.combat.maxHp, "$.combat.maxHp");
        c.integer(input.combat.currentHp, "$.combat.currentHp");
        c.nonNegativeInt(input.combat.tempHp, "$.combat.tempHp");
        c.nonNegativeInt(input.combat.ac, "$.combat.ac");
        // baseAc (Equipment & Armor R4.1) — ADDITIVE + OPTIONAL mundane-AC base;
        // a non-negative integer when present, absent on a legacy ledger.
        if ("baseAc" in input.combat && input.combat.baseAc !== undefined) {
            c.nonNegativeInt(input.combat.baseAc, "$.combat.baseAc");
        }
        c.integer(input.combat.initiativeMod, "$.combat.initiativeMod");
        c.nonNegativeInt(input.combat.speed, "$.combat.speed");
        // baseSpeed (Equipment & Armor Phase E) — ADDITIVE + OPTIONAL base walking
        // speed before any armor penalty; a non-negative integer when present.
        if ("baseSpeed" in input.combat && input.combat.baseSpeed !== undefined) {
            c.nonNegativeInt(input.combat.baseSpeed, "$.combat.baseSpeed");
        }
        if (typeof input.combat.currentHp === "number" &&
            typeof input.combat.maxHp === "number" &&
            input.combat.currentHp > input.combat.maxHp) {
            c.fail("$.combat.currentHp", "currentHp must not exceed maxHp");
        }
    }
    // inventory (R5.2 — "basic inventory" required; may be empty).
    if (c.array(input.inventory, "$.inventory")) {
        input.inventory.forEach((item, i) => {
            const p = `$.inventory[${i}]`;
            if (!c.isObject(item, p))
                return;
            c.id(item.itemId, `${p}.itemId`);
            c.string(item.name, `${p}.name`);
            c.nonNegativeInt(item.qty, `${p}.qty`);
            c.boolean(item.equipped, `${p}.equipped`);
            c.boolean(item.attunement, `${p}.attunement`);
        });
    }
    // conditions (R5.2 — required for combat bookkeeping; may be empty).
    checkConditions(c, input.conditions, "$.conditions");
    // concentration (R6.3) — optional for an npc; object or null when present.
    if ("concentration" in input && input.concentration !== undefined && input.concentration !== null) {
        if (c.isObject(input.concentration, "$.concentration")) {
            c.id(input.concentration.spellId, "$.concentration.spellId");
            checkStringArray(c, input.concentration.targets, "$.concentration.targets");
            if (input.concentration.endsAtRound !== null) {
                c.integer(input.concentration.endsAtRound, "$.concentration.endsAtRound");
            }
            // ongoingEffect (R2.10) — optional; when present, validates the recurring shape.
            if (input.concentration.ongoingEffect !== undefined) {
                checkOngoingEffect(c, input.concentration.ongoingEffect, "$.concentration.ongoingEffect");
            }
        }
    }
    // pendingRollContext (R31.16) — optional for an npc; object or null when present.
    if ("pendingRollContext" in input &&
        input.pendingRollContext !== undefined &&
        input.pendingRollContext !== null) {
        if (c.isObject(input.pendingRollContext, "$.pendingRollContext")) {
            c.string(input.pendingRollContext.type, "$.pendingRollContext.type");
            c.integer(input.pendingRollContext.dc, "$.pendingRollContext.dc");
            if ("skill" in input.pendingRollContext && input.pendingRollContext.skill !== undefined) {
                c.string(input.pendingRollContext.skill, "$.pendingRollContext.skill");
            }
        }
    }
    // knowledge (R9.2, R29.5) — optional for an npc; validated when present.
    if ("knowledge" in input && input.knowledge !== undefined) {
        if (c.array(input.knowledge, "$.knowledge")) {
            input.knowledge.forEach((node, i) => {
                const p = `$.knowledge[${i}]`;
                if (!c.isObject(node, p))
                    return;
                c.id(node.nodeId, `${p}.nodeId`);
                c.string(node.text, `${p}.text`);
            });
        }
    }
    // Full-PC-only fields (R5.2): OMITTABLE for an npc. If present, still validate
    // structure (a malformed block is a bug), but absence is legal.
    if ("resources" in input && input.resources !== undefined) {
        if (c.isObject(input.resources, "$.resources")) {
            checkPoolMap(c, input.resources.hitDice, "$.resources.hitDice");
            checkPoolMap(c, input.resources.spellSlots, "$.resources.spellSlots", SPELL_SLOT_LEVEL_KEYS);
            checkPoolMap(c, input.resources.classResources, "$.resources.classResources");
        }
    }
    if ("spells" in input && input.spells !== undefined) {
        if (c.isObject(input.spells, "$.spells")) {
            checkStringArray(c, input.spells.known, "$.spells.known");
            checkStringArray(c, input.spells.prepared, "$.spells.prepared");
        }
    }
    if ("currency" in input && input.currency !== undefined) {
        if (c.isObject(input.currency, "$.currency")) {
            c.nonNegativeInt(input.currency.gp, "$.currency.gp");
            c.nonNegativeInt(input.currency.sp, "$.currency.sp");
            c.nonNegativeInt(input.currency.cp, "$.currency.cp");
        }
    }
    if ("proficiencies" in input && input.proficiencies !== undefined) {
        if (c.isObject(input.proficiencies, "$.proficiencies")) {
            checkStringArray(c, input.proficiencies.skills, "$.proficiencies.skills");
            if (c.array(input.proficiencies.saves, "$.proficiencies.saves")) {
                input.proficiencies.saves.forEach((s, i) => c.enumOf(s, ABILITY_KEYS, `$.proficiencies.saves[${i}]`));
            }
            checkStringArray(c, input.proficiencies.weapons, "$.proficiencies.weapons");
            checkStringArray(c, input.proficiencies.tools, "$.proficiencies.tools");
            if ("expertise" in input.proficiencies && input.proficiencies.expertise !== undefined) {
                checkStringArray(c, input.proficiencies.expertise, "$.proficiencies.expertise");
            }
        }
    }
    if ("progression" in input && input.progression !== undefined) {
        if (c.isObject(input.progression, "$.progression")) {
            c.enumOf(input.progression.mode, ADVANCEMENT_MODES, "$.progression.mode");
            c.nonNegativeInt(input.progression.xp, "$.progression.xp");
            checkStringArray(c, input.progression.milestones, "$.progression.milestones");
        }
    }
    // engine-owned-mechanics optional fields (R5/R7/R2b/R2c/R2.9) — additive,
    // validated only when present; absence reads as the empty default. An npc may
    // carry deathSaves / damageModifiers (a monster with resistances) etc.
    checkEngineMechanicsFields(c, input);
    return finish(c, input);
}
// ---------------------------------------------------------------------------
// Encounter / Combat State (R5.4)
// ---------------------------------------------------------------------------
/** Validate Encounter/Combat State (R5.4, R6.1, R6.5). */
export function validateEncounterState(input) {
    const c = new Checker();
    if (!c.isObject(input, "$"))
        return finish(c, input);
    c.schemaVersion(input.schemaVersion, "$.schemaVersion");
    c.id(input.campaignId, "$.campaignId");
    c.id(input.encounterId, "$.encounterId");
    c.enumOf(input.status, ENCOUNTER_STATUSES, "$.status");
    c.nonNegativeInt(input.round, "$.round");
    c.nonNegativeInt(input.currentActorIndex, "$.currentActorIndex");
    if ("turnDeadline" in input && input.turnDeadline !== undefined) {
        c.nullableString(input.turnDeadline, "$.turnDeadline");
    }
    let orderLen = 0;
    if (c.array(input.initiativeOrder, "$.initiativeOrder")) {
        orderLen = input.initiativeOrder.length;
        input.initiativeOrder.forEach((e, i) => {
            const p = `$.initiativeOrder[${i}]`;
            if (!c.isObject(e, p))
                return;
            c.id(e.characterId, `${p}.characterId`);
            c.integer(e.initiative, `${p}.initiative`);
            c.boolean(e.isEnemy, `${p}.isEnemy`);
            // Optional rollback reconciliation marker (R16.3, Fix 3): present entries
            // omit it; a rolled-back-and-left-untouched character's slot carries
            // `absent: true`. Additive — absence of the field is valid.
            if ("absent" in e && e.absent !== undefined) {
                c.boolean(e.absent, `${p}.absent`);
            }
        });
        // currentActorIndex must point at a real slot in the order (R6.1). When the
        // order is empty EVERY index is out of range, so `currentActorIndex >=
        // orderLen` (which is `>= 0` at orderLen 0) is the correct rejection — the
        // old `orderLen > 0` guard inverted the intent at the empty-order boundary
        // and let e.g. `{ initiativeOrder: [], currentActorIndex: 5 }` pass.
        if (typeof input.currentActorIndex === "number" &&
            input.currentActorIndex >= orderLen) {
            c.fail("$.currentActorIndex", "currentActorIndex out of range of initiativeOrder");
        }
    }
    if (c.isObject(input.rangeBands, "$.rangeBands")) {
        for (const [key, band] of Object.entries(input.rangeBands)) {
            c.enumOf(band, RANGE_BANDS, `$.rangeBands.${key}`);
        }
    }
    return finish(c, input);
}
// ---------------------------------------------------------------------------
// Campaign State (R5.5)
// ---------------------------------------------------------------------------
/**
 * Validate Campaign State (R5.5). Enforces the fields task 4.1 calls out:
 * `seqToken` (R18.5), `advancementMode` (R12.1), and the `paused` flag (R21.6).
 */
export function validateCampaignState(input) {
    const c = new Checker();
    if (!c.isObject(input, "$"))
        return finish(c, input);
    c.schemaVersion(input.schemaVersion, "$.schemaVersion");
    c.id(input.campaignId, "$.campaignId");
    // Optional display name (set at creation; absent on pre-name campaigns).
    if ("name" in input && input.name !== undefined) {
        c.string(input.name, "$.name");
    }
    // Optional tone/genre descriptor (§3.5b; absent on pre-tone campaigns).
    if ("tone" in input && input.tone !== undefined) {
        c.string(input.tone, "$.tone");
    }
    c.enumOf(input.mode, INTERACTION_MODES, "$.mode");
    c.enumOf(input.advancementMode, ADVANCEMENT_MODES, "$.advancementMode");
    c.nonNegativeInt(input.seqToken, "$.seqToken");
    c.boolean(input.paused, "$.paused");
    c.string(input.rollingSummary, "$.rollingSummary");
    // Optional Summarizer watermark (R7.3): the highest log seq folded into the
    // rolling summary. Absent on a fresh campaign; a non-negative integer when set.
    if ("lastSummarizedSeq" in input && input.lastSummarizedSeq !== undefined) {
        c.nonNegativeInt(input.lastSummarizedSeq, "$.lastSummarizedSeq");
    }
    if (c.isObject(input.worldClock, "$.worldClock")) {
        // minuteOfEpoch is a SIGNED integer — negative is legal (time travel into the past).
        c.integer(input.worldClock.minuteOfEpoch, "$.worldClock.minuteOfEpoch");
        if (c.isObject(input.worldClock.calendar, "$.worldClock.calendar")) {
            const cal = input.worldClock.calendar;
            checkPositiveInt(c, cal.minutesPerHour, "$.worldClock.calendar.minutesPerHour");
            checkPositiveInt(c, cal.hoursPerDay, "$.worldClock.calendar.hoursPerDay");
            checkPositiveInt(c, cal.daysPerMonth, "$.worldClock.calendar.daysPerMonth");
            checkPositiveInt(c, cal.monthsPerYear, "$.worldClock.calendar.monthsPerYear");
            if ("epochYearLabel" in cal && cal.epochYearLabel !== undefined) {
                c.string(cal.epochYearLabel, "$.worldClock.calendar.epochYearLabel");
            }
            if ("dayPhaseBoundaries" in cal && cal.dayPhaseBoundaries !== undefined && c.isObject(cal.dayPhaseBoundaries, "$.worldClock.calendar.dayPhaseBoundaries")) {
                const b = cal.dayPhaseBoundaries;
                const vals = [];
                for (const key of ["dawn", "day", "dusk", "night"]) {
                    const path = `$.worldClock.calendar.dayPhaseBoundaries.${key}`;
                    if (c.number(b[key], path)) {
                        const v = b[key];
                        if (v < 0 || v >= 1)
                            c.fail(path, "expected a fraction in [0, 1)");
                        vals.push(v);
                    }
                }
                // The four boundaries must be strictly increasing dawn<day<dusk<night so
                // the day-phase derivation is well-ordered (R2).
                if (vals.length === 4 && !(vals[0] < vals[1] && vals[1] < vals[2] && vals[2] < vals[3])) {
                    c.fail("$.worldClock.calendar.dayPhaseBoundaries", "expected dawn < day < dusk < night");
                }
            }
        }
    }
    // Weather log (Time & Weather System R3) — optional, additive. Beyond the
    // per-entry shape, enforce the interval invariants the read + write paths assume
    // (two-model gate Medium #6): a closed entry's `endMin` must not precede its
    // `startMin` (no reverse interval), and a region has AT MOST ONE open entry (the
    // closeWeather applier + applySetWeather both close "the" open entry).
    if ("weatherLog" in input && input.weatherLog !== undefined && c.array(input.weatherLog, "$.weatherLog")) {
        const openByRegion = new Map(); // regionId → count of open entries
        input.weatherLog.forEach((e, i) => {
            const p = `$.weatherLog[${i}]`;
            if (!c.isObject(e, p))
                return;
            c.enumOf(e.kind, WEATHER_KINDS, `${p}.kind`);
            c.id(e.regionId, `${p}.regionId`);
            const startOk = c.integer(e.startMin, `${p}.startMin`); // signed (time travel)
            if ("endMin" in e && e.endMin !== undefined) {
                const endOk = c.integer(e.endMin, `${p}.endMin`);
                // A closed interval must not run backwards (an endMin < startMin would
                // silently corrupt every containment lookup).
                if (startOk && endOk && e.endMin < e.startMin) {
                    c.fail(`${p}.endMin`, "endMin must be >= startMin (no reverse interval)");
                }
            }
            else if (typeof e.regionId === "string") {
                openByRegion.set(e.regionId, (openByRegion.get(e.regionId) ?? 0) + 1);
            }
        });
        for (const [regionId, count] of openByRegion) {
            if (count > 1) {
                c.fail("$.weatherLog", `region ${regionId} has ${count} open weather entries (expected at most 1)`);
            }
        }
    }
    c.string(input.partyLocation, "$.partyLocation");
    if (c.array(input.activeQuests, "$.activeQuests")) {
        input.activeQuests.forEach((q, i) => {
            const p = `$.activeQuests[${i}]`;
            if (!c.isObject(q, p))
                return;
            c.id(q.id, `${p}.id`);
            c.string(q.title, `${p}.title`);
            c.string(q.status, `${p}.status`);
        });
    }
    if (c.array(input.members, "$.members")) {
        input.members.forEach((m, i) => {
            const p = `$.members[${i}]`;
            if (!c.isObject(m, p))
                return;
            c.id(m.userId, `${p}.userId`);
            c.id(m.characterId, `${p}.characterId`);
            c.string(m.presence, `${p}.presence`);
        });
    }
    // Optional party facing (Spatial Map System R1.3) — absent on a pre-spatial
    // campaign; a Direction enum value when set.
    if ("heading" in input && input.heading !== undefined) {
        c.enumOf(input.heading, DIRECTIONS, "$.heading");
    }
    // Optional party marching order (Spatial Map System R11, design §8A) — an
    // ordered list of characterIds, front to back. Absent = none declared
    // (absent-safe, R11.2); a string[] when set.
    if ("marchingOrder" in input && input.marchingOrder !== undefined) {
        checkStringArray(c, input.marchingOrder, "$.marchingOrder");
    }
    // customItems (Equipment & Armor R5) — the DM-authored campaign item store.
    // Optional; a well-formed array of CustomItemDef when present.
    if ("customItems" in input && input.customItems !== undefined) {
        checkCustomItems(c, input.customItems, "$.customItems");
    }
    // turnResumePlan (agenda turn model / Player-Owned Dice Phase 2) — the paused
    // multi-leg turn bridge. Optional; null or a well-formed plan when present.
    if ("turnResumePlan" in input && input.turnResumePlan !== undefined && input.turnResumePlan !== null) {
        checkTurnResumePlan(c, input.turnResumePlan, "$.turnResumePlan");
    }
    return finish(c, input);
}
/**
 * Validate `CampaignState.turnResumePlan` (agenda turn model) when present. The
 * plan is engine-written (not client-supplied), so this checks the structural
 * shape the resume leg relies on, not deep rule validity. The owed-die entries
 * reuse the generalized {@link PendingRollContext} optional fields.
 */
function checkTurnResumePlan(c, v, path) {
    if (!c.isObject(v, path))
        return;
    c.string(v.logicalTurnId, `${path}.logicalTurnId`);
    c.string(v.openingAction, `${path}.openingAction`);
    c.string(v.actingCharacterId, `${path}.actingCharacterId`);
    c.integer(v.currentActorIndexSnapshot, `${path}.currentActorIndexSnapshot`);
    c.string(v.priorNarration, `${path}.priorNarration`);
    if ("inFlightAction" in v && v.inFlightAction !== undefined && c.isObject(v.inFlightAction, `${path}.inFlightAction`)) {
        const a = v.inFlightAction;
        c.string(a.kind, `${path}.inFlightAction.kind`);
        c.string(a.attackerId, `${path}.inFlightAction.attackerId`);
        c.string(a.targetId, `${path}.inFlightAction.targetId`);
        c.string(a.weaponId, `${path}.inFlightAction.weaponId`);
        c.nonNegativeInt(a.swingsTotal, `${path}.inFlightAction.swingsTotal`);
        c.nonNegativeInt(a.swingsDone, `${path}.inFlightAction.swingsDone`);
    }
    if (c.array(v.history, `${path}.history`)) {
        v.history.forEach((e, i) => {
            const p = `${path}.history[${i}]`;
            if (!c.isObject(e, p))
                return;
            c.string(e.toolUseId, `${p}.toolUseId`);
            c.string(e.name, `${p}.name`);
            c.string(e.resultJSON, `${p}.resultJSON`);
        });
    }
    if (c.array(v.blockedOn, `${path}.blockedOn`)) {
        v.blockedOn.forEach((e, i) => {
            const p = `${path}.blockedOn[${i}]`;
            if (!c.isObject(e, p))
                return;
            // Generalized PendingRollContext: legacy type/dc still required; the agenda
            // fields are optional and engine-written.
            c.string(e.type, `${p}.type`);
            c.integer(e.dc, `${p}.dc`);
        });
    }
}
const CUSTOM_ITEM_CATEGORIES = ["armor", "weapon", "gear", "tool", "pack"];
/** Validate `CampaignState.customItems` (Equipment & Armor R5) when present. */
function checkCustomItems(c, v, path) {
    if (!c.array(v, path))
        return;
    v.forEach((raw, i) => {
        const p = `${path}[${i}]`;
        if (!c.isObject(raw, p))
            return;
        c.id(raw.itemId, `${p}.itemId`);
        c.string(raw.name, `${p}.name`);
        c.enumOf(raw.category, CUSTOM_ITEM_CATEGORIES, `${p}.category`);
        if (c.isObject(raw.cost, `${p}.cost`)) {
            for (const coin of ["gp", "sp", "cp"]) {
                if (coin in raw.cost && raw.cost[coin] !== undefined) {
                    c.nonNegativeInt(raw.cost[coin], `${p}.cost.${coin}`);
                }
            }
        }
        // weight is a NUMBER (lb) — fractional weights (0.5 lb) are valid, so this
        // must not demand an integer. Non-negative.
        if (c.number(raw.weight, `${p}.weight`) && raw.weight < 0) {
            c.fail(`${p}.weight`, "expected a non-negative number");
        }
        // armor/weapon mechanical sub-blocks are structural; validated loosely as
        // objects when present (the engine resolver reads them defensively).
        if ("armor" in raw && raw.armor !== undefined)
            c.isObject(raw.armor, `${p}.armor`);
        if ("weapon" in raw && raw.weapon !== undefined)
            c.isObject(raw.weapon, `${p}.weapon`);
    });
}
// ---------------------------------------------------------------------------
// Secret Ledger (R5.6, R9.2)
// ---------------------------------------------------------------------------
/**
 * Validate the Secret Ledger (R5.6). Enforces the `characterSecrets` array —
 * the campaign-held side of the knowledge/secrets split (R9.2).
 */
export function validateSecretLedger(input) {
    const c = new Checker();
    if (!c.isObject(input, "$"))
        return finish(c, input);
    c.schemaVersion(input.schemaVersion, "$.schemaVersion");
    c.id(input.campaignId, "$.campaignId");
    c.string(input.campaignBible, "$.campaignBible");
    if (c.array(input.characterSecrets, "$.characterSecrets")) {
        input.characterSecrets.forEach((s, i) => {
            const p = `$.characterSecrets[${i}]`;
            if (!c.isObject(s, p))
                return;
            c.id(s.aboutCharacterId, `${p}.aboutCharacterId`);
            c.string(s.text, `${p}.text`);
        });
    }
    if (c.array(input.plotHooks, "$.plotHooks")) {
        input.plotHooks.forEach((h, i) => {
            const p = `$.plotHooks[${i}]`;
            if (!c.isObject(h, p))
                return;
            c.id(h.id, `${p}.id`);
            c.string(h.text, `${p}.text`);
            c.string(h.status, `${p}.status`);
        });
    }
    return finish(c, input);
}
/**
 * Validate the Arc Ledger (§3.5a) — the DM-only story-spine state. Enforces the
 * schemaVersion, campaign scope, and the structural shape of every arc (kind/
 * scope/status/relevance enums, beats, branches, finales, seeds, consequences,
 * ref arrays). Optional fields (`beatType`, `advancesArcId`, `finales`,
 * `branchId`, `parentBranchId`, `leadsToFinaleId`, `loreRef`) are validated only
 * when present, so a thin seeded arc (mostly sketch, no finales yet) passes.
 */
export function validateArcLedger(input) {
    const c = new Checker();
    if (!c.isObject(input, "$"))
        return finish(c, input);
    c.schemaVersion(input.schemaVersion, "$.schemaVersion");
    c.id(input.campaignId, "$.campaignId");
    if (c.array(input.arcs, "$.arcs")) {
        input.arcs.forEach((a, i) => validateArc(c, a, `$.arcs[${i}]`));
    }
    // World-events (R9) — additive + optional: a campaign with none omits the
    // field. Validated only when present, so a pre-world-event ledger stays valid.
    if (input.worldEvents !== undefined && c.array(input.worldEvents, "$.worldEvents")) {
        input.worldEvents.forEach((e, i) => validateWorldEventInto(c, e, `$.worldEvents[${i}]`));
    }
    // Bootstrap provenance (campaign-spine-bootstrapping R3.6/R5.6) — additive +
    // optional: a legacy ledger omits it (treated as unknown provenance) and stays
    // valid. When present it must carry a `source` of exactly `model`|`fallback`
    // and a numeric `authoredAt`.
    if (input.bootstrap !== undefined && c.isObject(input.bootstrap, "$.bootstrap")) {
        c.enumOf(input.bootstrap.source, ARC_BOOTSTRAP_SOURCES, "$.bootstrap.source");
        c.number(input.bootstrap.authoredAt, "$.bootstrap.authoredAt");
    }
    return finish(c, input);
}
/** Opening-arc bootstrap provenance sources (R3.6/R5.6). */
const ARC_BOOTSTRAP_SOURCES = ["model", "fallback"];
/** The world-event lifecycle statuses (R9 — pending → fired → resolved/abandoned). */
const WORLD_EVENT_STATUSES = ["pending", "fired", "resolved", "abandoned"];
/**
 * Validate a {@link WorldEvent} (Story-Driven Map Authoring R9, design §7.2) —
 * the required `eventId`/`summary`/`status` + the discriminated `due` (either
 * `{ atClock:number }` or `{ trigger:string }`) + the optional `placeRef`/
 * `arcBeatRef`. Mirrors the other shared-types validators. Standalone (for the
 * world-event store write path) and folded into {@link validateArcLedger} via
 * {@link validateWorldEventInto}.
 */
export function validateWorldEvent(input) {
    const c = new Checker();
    if (!c.isObject(input, "$"))
        return finish(c, input);
    validateWorldEventInto(c, input, "$");
    return finish(c, input);
}
/** Validate one {@link WorldEvent}; pushes onto the shared checker. */
function validateWorldEventInto(c, e, p) {
    if (!c.isObject(e, p))
        return;
    c.id(e.eventId, `${p}.eventId`);
    c.string(e.summary, `${p}.summary`);
    c.enumOf(e.status, WORLD_EVENT_STATUSES, `${p}.status`);
    // `due` is a discriminated union: EXACTLY one of atClock (number) / trigger
    // (non-empty string). Guard the shape so a malformed due is rejected.
    if (c.isObject(e.due, `${p}.due`)) {
        const hasAtClock = "atClock" in e.due && e.due.atClock !== undefined;
        const hasTrigger = "trigger" in e.due && e.due.trigger !== undefined;
        if (hasAtClock && !hasTrigger) {
            c.number(e.due.atClock, `${p}.due.atClock`);
        }
        else if (hasTrigger && !hasAtClock) {
            c.id(e.due.trigger, `${p}.due.trigger`);
        }
        else {
            c.fail(`${p}.due`, "expected exactly one of `atClock` (number) or `trigger` (string)");
        }
    }
    optionalString(c, e, "placeRef", `${p}.placeRef`);
    optionalString(c, e, "arcBeatRef", `${p}.arcBeatRef`);
}
/** Validate one {@link Arc}; pushes onto the shared checker. */
function validateArc(c, a, p) {
    if (!c.isObject(a, p))
        return;
    c.id(a.arcId, `${p}.arcId`);
    c.enumOf(a.kind, ARC_KINDS, `${p}.kind`);
    c.enumOf(a.scope, ARC_SCOPES, `${p}.scope`);
    c.id(a.title, `${p}.title`);
    c.string(a.premise, `${p}.premise`);
    c.enumOf(a.status, ARC_STATUSES, `${p}.status`);
    c.enumOf(a.relevance, ARC_RELEVANCES, `${p}.relevance`);
    optionalString(c, a, "advancesArcId", `${p}.advancesArcId`);
    if (c.array(a.beats, `${p}.beats`)) {
        a.beats.forEach((b, i) => validateBeat(c, b, `${p}.beats[${i}]`));
    }
    if (c.array(a.branches, `${p}.branches`)) {
        a.branches.forEach((b, i) => validateBranch(c, b, `${p}.branches[${i}]`));
    }
    if (a.finales !== undefined && c.array(a.finales, `${p}.finales`)) {
        a.finales.forEach((f, i) => validateFinale(c, f, `${p}.finales[${i}]`));
    }
    if (c.array(a.seeds, `${p}.seeds`)) {
        a.seeds.forEach((s, i) => validateSeed(c, s, `${p}.seeds[${i}]`));
    }
    stringArray(c, a.characterRefs, `${p}.characterRefs`);
    stringArray(c, a.monsterRefs, `${p}.monsterRefs`);
    stringArray(c, a.loreRefs, `${p}.loreRefs`);
    stringArray(c, a.childArcIds, `${p}.childArcIds`);
    if (c.isObject(a.consequences, `${p}.consequences`)) {
        for (const k of ["onSuccess", "onPartial", "onFailure", "onSkip"]) {
            optionalString(c, a.consequences, k, `${p}.consequences.${k}`);
        }
    }
    c.enumOf(a.createdBy, ARC_CREATED_BY, `${p}.createdBy`);
    c.number(a.createdAt, `${p}.createdAt`);
    c.number(a.updatedAt, `${p}.updatedAt`);
    // lastProgressedSeqToken (campaign-spine-bootstrapping R9.7) — ADDITIVE + OPTIONAL
    // turns-elapsed marker for the stagnation nudge; a non-negative integer when
    // present, absent on a legacy ledger (reads as undefined → never flagged).
    if ("lastProgressedSeqToken" in a && a.lastProgressedSeqToken !== undefined) {
        c.nonNegativeInt(a.lastProgressedSeqToken, `${p}.lastProgressedSeqToken`);
    }
}
function validateBeat(c, b, p) {
    if (!c.isObject(b, p))
        return;
    c.id(b.beatId, `${p}.beatId`);
    c.string(b.summary, `${p}.summary`);
    c.enumOf(b.status, BEAT_STATUSES, `${p}.status`);
    c.enumOf(b.resolution, ARC_RESOLUTIONS, `${p}.resolution`);
    if (b.beatType !== undefined)
        c.enumOf(b.beatType, BEAT_TYPES, `${p}.beatType`);
    c.boolean(b.advancesSpine, `${p}.advancesSpine`);
    optionalString(c, b, "branchId", `${p}.branchId`);
    optionalString(c, b, "loreRef", `${p}.loreRef`);
    optionalString(c, b, "placeRef", `${p}.placeRef`); // arc↔place link (R3.1)
    if (b.placeKind !== undefined)
        c.enumOf(b.placeKind, PLACE_KINDS, `${p}.placeKind`); // place kind → generator/tier (R4.3)
    // Slot binding (Narrative Map Generation R4.3) — the brief-driven alternative to
    // placeRef; both optional strings, resolved to a node via the back-reference.
    optionalString(c, b, "placeAnchorId", `${p}.placeAnchorId`);
    optionalString(c, b, "placeSlotId", `${p}.placeSlotId`);
}
function validateBranch(c, b, p) {
    if (!c.isObject(b, p))
        return;
    c.id(b.branchId, `${p}.branchId`);
    if (b.parentBranchId !== undefined && b.parentBranchId !== null) {
        c.id(b.parentBranchId, `${p}.parentBranchId`);
    }
    c.string(b.summary, `${p}.summary`);
    c.enumOf(b.status, BRANCH_STATUSES, `${p}.status`);
    c.enumOf(b.resolution, ARC_RESOLUTIONS, `${p}.resolution`);
    stringArray(c, b.childBranchIds, `${p}.childBranchIds`);
    optionalString(c, b, "leadsToFinaleId", `${p}.leadsToFinaleId`);
}
function validateFinale(c, f, p) {
    if (!c.isObject(f, p))
        return;
    c.id(f.finaleId, `${p}.finaleId`);
    c.enumOf(f.tier, FINALE_TIERS, `${p}.tier`);
    c.string(f.condition, `${p}.condition`);
    c.string(f.summary, `${p}.summary`);
    c.boolean(f.reachable, `${p}.reachable`);
}
function validateSeed(c, s, p) {
    if (!c.isObject(s, p))
        return;
    c.id(s.seedId, `${p}.seedId`);
    c.string(s.summary, `${p}.summary`);
    c.enumOf(s.status, SEED_STATUSES, `${p}.status`);
    optionalString(c, s, "loreRef", `${p}.loreRef`);
}
/** Validate an optional string field: checked only when present + not undefined. */
function optionalString(c, obj, key, path) {
    if (key in obj && obj[key] !== undefined)
        c.string(obj[key], path);
}
/** Validate that a value is an array of strings. */
function stringArray(c, v, path) {
    if (!c.array(v, path))
        return;
    v.forEach((item, i) => c.string(item, `${path}[${i}]`));
}
// ---------------------------------------------------------------------------
// Issue reports (R-issues)
// ---------------------------------------------------------------------------
/**
 * Validate a reported {@link Issue}. Enforces the required fields + the status
 * enum + the append-only `notes` shape (each note's optional `newStatus` is
 * checked only when present). The campaign-anchor fields (`campaignId`,
 * `anchorSeq`, `anchorTurnId`) are optional — a general (non-campaign) report
 * omits them — and are validated only when present.
 */
export function validateIssue(input) {
    const c = new Checker();
    if (!c.isObject(input, "$"))
        return finish(c, input);
    c.schemaVersion(input.schemaVersion, "$.schemaVersion");
    c.id(input.issueId, "$.issueId");
    c.id(input.reporterUserId, "$.reporterUserId");
    c.string(input.text, "$.text");
    c.enumOf(input.status, ISSUE_STATUSES, "$.status");
    c.number(input.createdAt, "$.createdAt");
    c.number(input.updatedAt, "$.updatedAt");
    if (c.array(input.notes, "$.notes")) {
        input.notes.forEach((n, i) => {
            const p = `$.notes[${i}]`;
            if (!c.isObject(n, p))
                return;
            c.number(n.timestamp, `${p}.timestamp`);
            c.id(n.author, `${p}.author`);
            c.string(n.text, `${p}.text`);
            if (n.newStatus !== undefined)
                c.enumOf(n.newStatus, ISSUE_STATUSES, `${p}.newStatus`);
        });
    }
    // Campaign anchor — present together for an in-campaign report, absent for a
    // general one. Validated only when present.
    optionalString(c, input, "campaignId", "$.campaignId");
    if (input.anchorSeq !== undefined)
        c.nonNegativeInt(input.anchorSeq, "$.anchorSeq");
    optionalString(c, input, "anchorTurnId", "$.anchorTurnId");
    // The server build stamp — optional, and both subfields optional. Validated
    // only when present.
    if (input.reportedBuild !== undefined && c.isObject(input.reportedBuild, "$.reportedBuild")) {
        optionalString(c, input.reportedBuild, "gitSha", "$.reportedBuild.gitSha");
        optionalString(c, input.reportedBuild, "builtAt", "$.reportedBuild.builtAt");
    }
    // Origin + coalescing (R-telemetry) — present only on an auto-filed issue; a
    // human report omits them (and reads as `source: "user"`). Validated only when
    // present so pre-existing issues stay valid.
    if (input.source !== undefined)
        c.enumOf(input.source, ISSUE_SOURCES, "$.source");
    optionalString(c, input, "signature", "$.signature");
    if (input.occurrenceCount !== undefined) {
        c.nonNegativeInt(input.occurrenceCount, "$.occurrenceCount");
    }
    if (input.lastOccurredAt !== undefined)
        c.number(input.lastOccurredAt, "$.lastOccurredAt");
    if (input.rev !== undefined)
        c.nonNegativeInt(input.rev, "$.rev");
    // The client (SPA) build stamp — optional, both subfields optional. Distinct
    // from reportedBuild (the server build). Validated only when present.
    if (input.clientBuild !== undefined && c.isObject(input.clientBuild, "$.clientBuild")) {
        optionalString(c, input.clientBuild, "gitSha", "$.clientBuild.gitSha");
        optionalString(c, input.clientBuild, "builtAt", "$.clientBuild.builtAt");
    }
    return finish(c, input);
}
// ---------------------------------------------------------------------------
// Spatial Map System (Big Phase 1.5)
//
// Structural validators for the campaign-scoped spatial graph. Required fields
// are checked; optional additive fields follow the existing convention (checked
// only when present). These are the structural guard the spatial write path runs
// before applying a graph mutation (R4); SRD legality of a DC/check is a separate
// deeper concern owned by the write-path validator.
// ---------------------------------------------------------------------------
const DIRECTIONS = [
    "north", "south", "east", "west",
    "northeast", "northwest", "southeast", "southwest",
    "up", "down",
];
const HIERARCHY_LEVELS = ["macro", "meso", "micro"];
const SHAPE_PROFILES = [
    "square", "rectangular", "circular", "octagonal",
    "irregular_cavern", "meandering_tunnel", "chasm_ledge",
];
const ILLUMINATIONS = ["bright", "dim", "darkness"];
const BEARING_DEFLECTIONS = [
    "straight", "gentle_curve_left", "gentle_curve_right", "sharp_left", "sharp_right", "meandering",
];
const BARRIER_STATUSES = ["open", "closed", "locked", "barred", "spiked", "destroyed"];
const EDGE_STATES = ["hidden", "discovered"];
const MOVEMENT_MODES = ["walk", "climb", "swim", "fly"];
/** Validate a {@link Coordinate} inline (hidden x,y,z, integers). */
function checkCoordinate(c, v, path) {
    if (!c.isObject(v, path))
        return;
    c.integer(v.x, `${path}.x`);
    c.integer(v.y, `${path}.y`);
    c.integer(v.z, `${path}.z`);
}
/** Validate a {@link SpatialNode} (R1, R8, R10, R12). */
export function validateSpatialNode(input) {
    const c = new Checker();
    if (!c.isObject(input, "$"))
        return finish(c, input);
    c.id(input.nodeId, "$.nodeId");
    c.enumOf(input.hierarchyLevel, HIERARCHY_LEVELS, "$.hierarchyLevel");
    checkCoordinate(c, input.coord, "$.coord");
    c.boolean(input.isAnchor, "$.isAnchor");
    c.enumOf(input.shapeProfile, SHAPE_PROFILES, "$.shapeProfile");
    c.enumOf(input.illumination, ILLUMINATIONS, "$.illumination");
    c.integer(input.renderSeed, "$.renderSeed");
    c.boolean(input.discovered, "$.discovered");
    // DM-narrated display name, decoupled from nodeId (Spatial Sight + Traverse-and-Name).
    // Additive + optional — a legacy/unnamed node omits it.
    if ("name" in input && input.name !== undefined)
        c.string(input.name, "$.name");
    if ("dims" in input && input.dims !== undefined && c.isObject(input.dims, "$.dims")) {
        c.nonNegativeInt(input.dims.widthFt, "$.dims.widthFt");
        c.nonNegativeInt(input.dims.lengthFt, "$.dims.lengthFt");
        if ("ceilingFt" in input.dims && input.dims.ceilingFt !== undefined) {
            c.nonNegativeInt(input.dims.ceilingFt, "$.dims.ceilingFt");
        }
    }
    if ("capacity" in input && input.capacity !== undefined) {
        c.nonNegativeInt(input.capacity, "$.capacity");
    }
    if ("stateFlags" in input && input.stateFlags !== undefined && c.isObject(input.stateFlags, "$.stateFlags")) {
        for (const [k, val] of Object.entries(input.stateFlags))
            c.boolean(val, `$.stateFlags.${k}`);
    }
    if ("entities" in input && input.entities !== undefined && c.array(input.entities, "$.entities")) {
        input.entities.forEach((e, i) => {
            const p = `$.entities[${i}]`;
            if (!c.isObject(e, p))
                return;
            c.id(e.entityId, `${p}.entityId`);
            if ("characterId" in e && e.characterId !== undefined)
                c.id(e.characterId, `${p}.characterId`);
            if ("acousticTag" in e && e.acousticTag !== undefined)
                c.string(e.acousticTag, `${p}.acousticTag`);
            if ("patrolRoute" in e && e.patrolRoute !== undefined)
                checkStringArray(c, e.patrolRoute, `${p}.patrolRoute`);
        });
    }
    if ("contains" in input && input.contains !== undefined)
        checkStringArray(c, input.contains, "$.contains");
    if ("destroyed" in input && input.destroyed !== undefined)
        c.boolean(input.destroyed, "$.destroyed");
    if ("trapped" in input && input.trapped !== undefined)
        c.boolean(input.trapped, "$.trapped");
    // Story-Driven Map Authoring: arc↔place link + pre-authoring (all additive/optional).
    if ("arcRefs" in input && input.arcRefs !== undefined)
        checkStringArray(c, input.arcRefs, "$.arcRefs");
    if ("resolution" in input && input.resolution !== undefined) {
        c.enumOf(input.resolution, ARC_RESOLUTIONS, "$.resolution"); // sketch|developing|concrete (shared with arcs)
    }
    if ("provisional" in input && input.provisional !== undefined)
        c.boolean(input.provisional, "$.provisional");
    // Staleness anchor (R9.4/R9.6) — the in-world clock of last party presence
    // (a signed integer minuteOfEpoch; time travel is legal). Optional + additive.
    if ("lastVisitedAt" in input && input.lastVisitedAt !== undefined)
        c.integer(input.lastVisitedAt, "$.lastVisitedAt");
    // Place KIND (R4.3) — the generator/tier selector stamped at mint; optional + additive.
    if ("placeKind" in input && input.placeKind !== undefined)
        c.enumOf(input.placeKind, PLACE_KINDS, "$.placeKind");
    // Narrative Map Generation (brief-driven, unit-DAG, progressive). All additive +
    // optional; validated only when present so a legacy node stays valid.
    if ("briefOrigin" in input && input.briefOrigin !== undefined)
        c.string(input.briefOrigin, "$.briefOrigin");
    if ("briefVersion" in input && input.briefVersion !== undefined)
        c.nonNegativeInt(input.briefVersion, "$.briefVersion");
    if ("backRefs" in input && input.backRefs !== undefined && c.isObject(input.backRefs, "$.backRefs")) {
        // A MAP anchorId → slotId (both non-empty strings); a node may be a unit in
        // more than one brief, so the map can carry multiple anchors.
        for (const [anchorId, slotId] of Object.entries(input.backRefs)) {
            if (anchorId.length === 0)
                c.fail("$.backRefs", "backRefs key (anchorId) must be a non-empty string");
            c.string(slotId, `$.backRefs.${anchorId}`);
        }
    }
    if ("regionId" in input && input.regionId !== undefined)
        c.string(input.regionId, "$.regionId");
    if ("construction" in input && input.construction !== undefined)
        c.string(input.construction, "$.construction");
    if ("role" in input && input.role !== undefined)
        c.string(input.role, "$.role");
    if ("salience" in input && input.salience !== undefined) {
        c.enumOf(input.salience, SALIENCES, "$.salience");
    }
    if ("salienceProvenance" in input && input.salienceProvenance !== undefined) {
        c.enumOf(input.salienceProvenance, SALIENCE_PROVENANCES, "$.salienceProvenance");
    }
    if ("tombstoned" in input && input.tombstoned !== undefined)
        c.boolean(input.tombstoned, "$.tombstoned");
    // ANCHOR-only bookkeeping (optimistic lock + abandoned set). Optional + additive.
    if ("realizedRev" in input && input.realizedRev !== undefined)
        c.nonNegativeInt(input.realizedRev, "$.realizedRev");
    if ("abandonedUnits" in input && input.abandonedUnits !== undefined) {
        checkStringArray(c, input.abandonedUnits, "$.abandonedUnits");
    }
    return finish(c, input);
}
/** Intrinsic salience of a brief sub-place (Narrative Map Generation R4.1). */
const SALIENCES = ["ordinary", "salient"];
/** Where a node's salience/role came from — intrinsic brief vs arc overlay (R4.3). */
const SALIENCE_PROVENANCES = ["brief", "arc"];
/** Validate a {@link SpatialEdge} (R1, R5, R6, R7, R9, R13). */
export function validateSpatialEdge(input) {
    const c = new Checker();
    if (!c.isObject(input, "$"))
        return finish(c, input);
    c.id(input.edgeId, "$.edgeId");
    c.id(input.from, "$.from");
    c.id(input.to, "$.to");
    c.enumOf(input.direction, DIRECTIONS, "$.direction");
    c.enumOf(input.edgeState, EDGE_STATES, "$.edgeState");
    if ("bearingDeflection" in input && input.bearingDeflection !== undefined) {
        c.enumOf(input.bearingDeflection, BEARING_DEFLECTIONS, "$.bearingDeflection");
    }
    if ("barrier" in input && input.barrier !== undefined && c.isObject(input.barrier, "$.barrier")) {
        c.string(input.barrier.barrierType, "$.barrier.barrierType");
        c.enumOf(input.barrier.status, BARRIER_STATUSES, "$.barrier.status");
        if ("lockDc" in input.barrier && input.barrier.lockDc !== undefined)
            c.nonNegativeInt(input.barrier.lockDc, "$.barrier.lockDc");
        if ("breakDc" in input.barrier && input.barrier.breakDc !== undefined)
            c.nonNegativeInt(input.barrier.breakDc, "$.barrier.breakDc");
    }
    if ("discoveryDc" in input && input.discoveryDc !== undefined)
        c.nonNegativeInt(input.discoveryDc, "$.discoveryDc");
    if ("movementMultiplier" in input && input.movementMultiplier !== undefined)
        c.number(input.movementMultiplier, "$.movementMultiplier");
    if ("requiredMovementMode" in input && input.requiredMovementMode !== undefined && c.array(input.requiredMovementMode, "$.requiredMovementMode")) {
        input.requiredMovementMode.forEach((m, i) => c.enumOf(m, MOVEMENT_MODES, `$.requiredMovementMode[${i}]`));
    }
    if ("traversalCheck" in input && input.traversalCheck !== undefined && c.isObject(input.traversalCheck, "$.traversalCheck")) {
        c.string(input.traversalCheck.skill, "$.traversalCheck.skill");
        c.nonNegativeInt(input.traversalCheck.dc, "$.traversalCheck.dc");
    }
    if ("environmentalHazard" in input && input.environmentalHazard !== undefined)
        c.string(input.environmentalHazard, "$.environmentalHazard");
    if ("occlusion" in input && input.occlusion !== undefined && c.isObject(input.occlusion, "$.occlusion")) {
        c.boolean(input.occlusion.sound, "$.occlusion.sound");
        c.boolean(input.occlusion.light, "$.occlusion.light");
        c.boolean(input.occlusion.gas, "$.occlusion.gas");
    }
    if ("projectsTo" in input && input.projectsTo !== undefined)
        c.id(input.projectsTo, "$.projectsTo");
    if ("destroyed" in input && input.destroyed !== undefined)
        c.boolean(input.destroyed, "$.destroyed");
    // Single-file edge flag (Spatial Map System R11.5, design §8A.2) — a door /
    // narrow ledge the party passes one at a time. Optional + additive.
    if ("singleFile" in input && input.singleFile !== undefined)
        c.boolean(input.singleFile, "$.singleFile");
    return finish(c, input);
}
//# sourceMappingURL=validators.js.map